import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useBuilderStore } from '../stores/builderStore';
import { getForm, updateForm, publishForm, createLiveQuizSession } from '../lib/api';
import LiveQuizBuilder from '../components/builder/LiveQuizBuilder';
import { Skeleton } from '../components/ui/Skeleton';

export default function LiveQuizBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { initializeForm, formName, formSchema, hasUnsavedChanges, markAsSaved, reset } = useBuilderStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPublishSuccess, setShowPublishSuccess] = useState<{
    sessionId: string;
    gamePin: string;
    playerUrl: string;
    hostUrl: string;
    resultsUrl: string;
  } | null>(null);

  useEffect(() => {
    if (!id) return;

    async function loadForm() {
      try {
        setLoading(true);
        const form = await getForm(id!);
        initializeForm(form.id, form.name, form.schema);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load quiz');
      } finally {
        setLoading(false);
      }
    }

    loadForm();

    return () => {
      reset();
    };
  }, [id, initializeForm, reset]);

  async function handleSave() {
    if (!id) return;

    try {
      setSaving(true);
      setError(null);
      await updateForm(id, { schema: formSchema });
      markAsSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save quiz');
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    if (!id) return;

    if (formSchema.fields.length === 0) {
      setError('Add at least one question before publishing');
      return;
    }

    // Validate questions
    const invalidQuestions = formSchema.fields.filter(field => {
      const hasCorrectAnswer = field.properties.correctAnswer &&
        (Array.isArray(field.properties.correctAnswer)
          ? field.properties.correctAnswer.length > 0
          : field.properties.correctAnswer !== undefined);
      return !hasCorrectAnswer;
    });

    if (invalidQuestions.length > 0) {
      setError(`${invalidQuestions.length} question(s) have no correct answer marked`);
      return;
    }

    try {
      setPublishing(true);
      setError(null);

      // Save first if there are unsaved changes
      if (hasUnsavedChanges) {
        await updateForm(id, { schema: formSchema });
        markAsSaved();
      }

      // Publish the form to make it available
      const publishResult = await publishForm(id);

      // Create a live quiz session - this generates the game PIN and registers the session
      const sessionResult = await createLiveQuizSession(id);

      const origin = window.location.origin;
      setShowPublishSuccess({
        sessionId: sessionResult.sessionId,
        gamePin: sessionResult.gamePin,
        playerUrl: `${origin}/play`,
        hostUrl: `${origin}/live/session/${sessionResult.sessionId}/host`,
        resultsUrl: `${origin}${publishResult.resultsUrl}`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish quiz');
    } finally {
      setPublishing(false);
    }
  }

  function handleStartGame() {
    if (!showPublishSuccess) return;
    navigate(`/live/session/${showPublishSuccess.sessionId}/host`);
  }

  function handleOpenHostPage() {
    if (!showPublishSuccess) return;
    window.open(`/live/session/${showPublishSuccess.sessionId}/host`, '_blank');
  }

  if (loading) {
    return (
      <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="w-6 h-6 rounded" />
            <Skeleton className="h-6 w-48" />
          </div>
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-20 rounded-lg" />
            <Skeleton className="h-10 w-24 rounded-lg" />
          </div>
        </header>
        <div className="flex-1 flex">
          <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg mb-3" />
            ))}
          </div>
          <div className="flex-1 p-8">
            <Skeleton className="h-10 w-64 mb-6" />
            <Skeleton className="h-32 w-full rounded-lg mb-4" />
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !formName) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <Link to="/formspace" className="btn-primary">
            Back to Forms
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/formspace" className="text-white/80 hover:text-white transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">{formName}</h1>
              <span className="text-xs text-white/70">Live Quiz</span>
            </div>
          </div>
          {hasUnsavedChanges && (
            <span className="text-xs text-white bg-white/20 px-2 py-1 rounded">
              Unsaved changes
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          {error && (
            <span className="text-sm text-white bg-red-500/80 px-3 py-1 rounded">{error}</span>
          )}

          <button
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>

          <button
            onClick={handlePublish}
            disabled={publishing || formSchema.fields.length === 0}
            className="px-4 py-2 bg-white text-orange-600 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
          >
            {publishing ? 'Publishing...' : 'Publish & Play'}
          </button>
        </div>
      </header>

      {/* Builder */}
      <LiveQuizBuilder formId={id} />

      {/* Publish Success Modal */}
      {showPublishSuccess && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-8 text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Quiz Published!</h2>
              <p className="text-white/80">Your live quiz is ready to play</p>
            </div>

            {/* Game PIN Section */}
            <div className="px-6 py-6 border-b border-gray-200 dark:border-gray-700">
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Game PIN</p>
                <div className="text-5xl font-bold tracking-wider text-gray-900 dark:text-white mb-3">
                  {showPublishSuccess.gamePin}
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(showPublishSuccess.gamePin)}
                  className="text-sm text-orange-600 hover:text-orange-700 dark:text-orange-400 font-medium"
                >
                  Copy PIN
                </button>
              </div>
            </div>

            {/* Instructions */}
            <div className="px-6 py-6 bg-gray-50 dark:bg-gray-900/50">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">How to Play</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-orange-600 dark:text-orange-400 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Share the Game PIN</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Tell players to go to <span className="font-mono bg-gray-200 dark:bg-gray-700 px-1 rounded">{window.location.origin}/play</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-orange-600 dark:text-orange-400 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Players Enter PIN</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      They enter the Game PIN and choose a nickname
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-orange-600 dark:text-orange-400 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Start the Game</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Once players join, click "Start Game" on the host screen
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* URLs Section */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Links</h4>

              {/* Player URL */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 w-24 flex-shrink-0">
                  <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Players:</span>
                </div>
                <code className="flex-1 text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded truncate">
                  {showPublishSuccess.playerUrl}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(showPublishSuccess.playerUrl)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>

              {/* Host/Moderator URL */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 w-24 flex-shrink-0">
                  <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Host:</span>
                </div>
                <code className="flex-1 text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded truncate">
                  {showPublishSuccess.hostUrl}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(showPublishSuccess.hostUrl)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={handleOpenHostPage}
                  className="text-orange-500 hover:text-orange-600 flex-shrink-0"
                  title="Open in new tab"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
              </div>

              {/* Results URL */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 w-24 flex-shrink-0">
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Results:</span>
                </div>
                <code className="flex-1 text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded truncate">
                  {showPublishSuccess.resultsUrl}
                </code>
                <button
                  onClick={() => navigator.clipboard.writeText(showPublishSuccess.resultsUrl)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 flex gap-3">
              <button
                onClick={() => setShowPublishSuccess(null)}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
              >
                Edit Quiz
              </button>
              <button
                onClick={handleStartGame}
                className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Start Game
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
